import type { HttpClient } from './http.js'
import { measureRoundTrip, type AppSocket, type SocketManager } from './socket.js'
import { patch, readonly, writable, type ReadableStore } from './store.js'

/**
 * Is the server reachable, and are its dependencies up: the system check.
 *
 * Whether the API answered is kept apart from what it said. "The backend is
 * unreachable" and "the backend says Redis is down" are different problems
 * with different fixes; one red dot for both sends you debugging the wrong
 * service.
 */

export type SocketStatus = 'idle' | 'connecting' | 'connected' | 'disconnected'
export type ApiStatus = 'unknown' | 'reachable' | 'unreachable'
/** A dependency's state. `unknown` means we could not ask, not that it failed. */
export type CheckState = 'up' | 'down' | 'unknown'

export interface ReadinessReport {
  status: 'ready' | 'not_ready'
  checks: { redis: boolean; database: boolean; migrations: boolean }
}

export interface SystemCheckState {
  status: SocketStatus
  socketId: string | null
  socketError: string | null
  latencyMs: number | null
  apiStatus: ApiStatus
  apiError: string | null
  readiness: ReadinessReport | null
}

/** A dependency's state in a system check's snapshot. */
export function checkState(s: SystemCheckState, name: keyof ReadinessReport['checks']): CheckState {
  const value = s.readiness?.checks[name]
  if (s.apiStatus !== 'reachable' || value === undefined) return 'unknown'
  return value ? 'up' : 'down'
}

export class SystemCheck {
  private readonly store = writable<SystemCheckState>({
    status: 'idle',
    socketId: null,
    socketError: null,
    latencyMs: null,
    apiStatus: 'unknown',
    apiError: null,
    readiness: null,
  })
  readonly state: ReadableStore<SystemCheckState> = readonly(this.store)
  private socket: AppSocket | null = null

  /**
   * @param serverLabel Where the server is, in words, for the error messages:
   *   a dev proxy's target, say, so a port mismatch is visible.
   */
  constructor(
    private readonly http: HttpClient,
    private readonly sockets: SocketManager,
    readonly serverLabel: string,
  ) {}

  /** A dependency's state from the last readiness report. */
  checkState(name: keyof ReadinessReport['checks']): CheckState {
    return checkState(this.store.get(), name)
  }

  /**
   * Connects anonymously: the check only exercises ping/pong. The server
   * allows the connection but grants it no identity, so it cannot join a
   * room.
   */
  connect(): void {
    if (this.socket) return
    patch(this.store, { status: 'connecting' })
    const sock = this.sockets.connect(() => null)
    this.socket = sock

    sock.on('connect', () =>
      patch(this.store, { status: 'connected', socketId: sock.id ?? null, socketError: null }),
    )
    sock.on('disconnect', (reason) =>
      patch(this.store, {
        status: 'disconnected',
        socketId: null,
        socketError: `Disconnected: ${reason}`,
      }),
    )
    sock.on('connect_error', (err) =>
      patch(this.store, {
        status: 'disconnected',
        socketError:
          `Could not open the websocket (${err.message || 'no detail'}) to ${this.serverLabel} — ` +
          `check the backend is listening there.`,
      }),
    )
    sock.on('server:shutdown', ({ reason, reconnectInMs }) =>
      patch(this.store, {
        socketError: `Server restarting (${reason}). Reconnecting in ${reconnectInMs}ms.`,
      }),
    )
  }

  async ping(): Promise<void> {
    if (!this.socket) return
    try {
      patch(this.store, { latencyMs: await measureRoundTrip(this.socket), socketError: null })
    } catch (err) {
      patch(this.store, {
        latencyMs: null,
        socketError: err instanceof Error ? err.message : String(err),
      })
    }
  }

  async checkReadiness(): Promise<void> {
    try {
      const res = await fetch(this.http.rootUrl('/readyz'), {
        headers: { accept: 'application/json' },
      })
      // 503 is a valid, informative answer: the API is reachable and telling
      // us a dependency is down. Only a transport failure means unreachable.
      patch(this.store, {
        apiStatus: 'reachable',
        apiError: null,
        readiness: (await res.json()) as ReadinessReport,
      })
    } catch (err) {
      patch(this.store, {
        apiStatus: 'unreachable',
        readiness: null,
        apiError:
          `The API at ${this.serverLabel} did not answer. Is the backend running, and does ` +
          `PORT in .env match? (${err instanceof Error ? err.message : String(err)})`,
      })
    }
  }
}
